import { isLocale, type Locale } from "@/kernel/locale";
import {
  LEAD_INTERESTS,
  LEAD_LIMITS,
  LEAD_TEXT_FIELDS,
  type LeadFieldError,
  type LeadFieldErrors,
  type LeadInterest,
  type LeadRequiredField,
  type LeadSubmission,
  type LeadTextField,
} from "../contracts";

// Rules of a connect-form request (prototype/home/v1). Deterministic and
// environment-neutral: the form checks the field rules before sending, and the
// server checks the same rules again, plus lengths, interests, locale and the
// honeypot, before a lead is stored (DSH-015).

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_CHARACTERS = /^[+\d\s()-]+$/;
const MIN_PHONE_DIGITS = 10;
const MIN_TEXT = 2;
// Control characters other than line breaks and tabs; never stored.
const CONTROL = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F\u2028\u2029]/g;
const LINE_BREAKS = /[\r\n\t]+/g;

/** A work email, or a phone number of at least ten digits written with digits, spaces, +, ( ) and -. */
export function isLeadContact(value: string): boolean {
  const text = value.trim();
  if (EMAIL.test(text)) return true;
  return text.replace(/\D/g, "").length >= MIN_PHONE_DIGITS && PHONE_CHARACTERS.test(text);
}

// Characters as PostgreSQL's char_length() counts them (code points, so "😀" is one).
const characters = (value: string) => Array.from(value.trim()).length;

export function leadFieldError(field: LeadRequiredField, value: string): LeadFieldError | null {
  switch (field) {
    case "company":
      return characters(value) >= MIN_TEXT ? null : "company_required";
    case "name":
      return characters(value) >= MIN_TEXT ? null : "name_required";
    case "contact":
      return isLeadContact(value) ? null : "contact_invalid";
  }
}

/** Every required-field problem of a request; empty when it may be sent. */
export function leadSubmissionErrors(submission: Pick<LeadSubmission, LeadRequiredField>): Partial<Record<LeadRequiredField, LeadFieldError>> {
  const errors: Partial<Record<LeadRequiredField, LeadFieldError>> = {};
  for (const field of ["company", "name", "contact"] as const) {
    const error = leadFieldError(field, submission[field]);
    if (error) errors[field] = error;
  }
  return errors;
}

/** A checked request: trimmed, NFC-normalized, without control characters; empty optional fields are null. */
export type CleanLead = {
  locale: Locale;
  company: string;
  name: string;
  role: string | null;
  contact: string;
  sphere: string | null;
  pointsCount: string | null;
  interests: LeadInterest[];
  comment: string | null;
};

export type ParsedLead =
  | { ok: true; lead: CleanLead; honeypot: boolean }
  | { ok: false; fields: LeadFieldErrors; problem: "fields" | "shape" };

function clean(value: unknown, field: LeadTextField): string | null {
  if (value === undefined || value === null) return "";
  if (typeof value !== "string") return null;
  const text = value.normalize("NFC").replace(CONTROL, "");
  // Only the comment keeps its line breaks.
  return (field === "comment" ? text : text.replace(LINE_BREAKS, " ")).trim();
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

/** The server's check of an untrusted request body. */
export function parseLeadSubmission(input: unknown): ParsedLead {
  if (!isRecord(input)) return { ok: false, fields: {}, problem: "shape" };
  if (!isLocale(input.locale)) return { ok: false, fields: {}, problem: "shape" };
  const interests = input.interests ?? [];
  if (!Array.isArray(interests) || interests.length > 20 || !interests.every((item) => typeof item === "string")) {
    return { ok: false, fields: {}, problem: "shape" };
  }
  if (input.website !== undefined && typeof input.website !== "string") return { ok: false, fields: {}, problem: "shape" };

  const values = {} as Record<LeadTextField, string>;
  const fields: LeadFieldErrors = {};
  for (const field of LEAD_TEXT_FIELDS) {
    const value = clean(input[field], field);
    if (value === null) return { ok: false, fields: {}, problem: "shape" };
    values[field] = value;
    if (Array.from(value).length > LEAD_LIMITS[field]) fields[field] = "too_long";
  }
  for (const [field, error] of Object.entries(leadSubmissionErrors(values))) {
    fields[field as LeadRequiredField] ??= error;
  }
  if (Object.keys(fields).length) return { ok: false, fields, problem: "fields" };

  const picked = new Set(interests);
  const optional = (value: string) => (value === "" ? null : value);
  return {
    ok: true,
    honeypot: typeof input.website === "string" && input.website.trim() !== "",
    lead: {
      locale: input.locale,
      company: values.company,
      name: values.name,
      role: optional(values.role),
      contact: values.contact,
      sphere: optional(values.sphere),
      pointsCount: optional(values.pointsCount),
      interests: LEAD_INTERESTS.filter((interest) => picked.has(interest)),
      comment: optional(values.comment),
    },
  };
}
